import { BrowserModule } from '@angular/platform-browser';
import { ErrorHandler, NgModule, Injectable, Injector } from '@angular/core';
import { HttpClientModule } from '@angular/common/http';
import { MyApp } from './app.component';

import { Camera } from '@ionic-native/camera';
import { Crop } from '@ionic-native/crop';
import { File } from '@ionic-native/file';
import { Pro } from '@ionic/pro';
import { IonicApp, IonicErrorHandler, IonicModule, IonicPageModule } from 'ionic-angular';
import { StatusBar } from '@ionic-native/status-bar';
import { SocialSharing } from '@ionic-native/social-sharing';
import { MediaCapture } from '@ionic-native/media-capture';
import { Chooser } from '@ionic-native/chooser/ngx';
import { VideoEditor } from '@ionic-native/video-editor';
import { Shake } from '@ionic-native/shake';
import { AndroidPermissions } from '@ionic-native/android-permissions';
import { LocationAccuracy } from '@ionic-native/location-accuracy';
import { ScreenOrientation } from '@ionic-native/screen-orientation';
// import { Contacts } from '@ionic-native/contacts';
import { AppVersion } from '@ionic-native/app-version';
import { Deeplinks } from '@ionic-native/deeplinks';

// Page modules
//   - Only a few need to be imported, depending on if we referene something
//     specifically, not just use navigation
import { HomePageModule } from '../pages/home/home/home.module';

// Pipes
import { PipesModule } from '../pipes/pipes.module';

// Providers
import { DecimalPipe } from '@angular/common';
import { EnvVariables } from '../environments/environment';
import { CrudApiProvider } from '../providers/crud-api/crud-api';
import { AuthProvider } from '../providers/auth/auth';
import { ToolsProvider } from '../providers/tools/tools';
import { ConfigProvider } from '../providers/config/config';
import { UserProvider } from '../providers/user/user';
import { TripService } from '../providers/trip-service/trip-service';
import { ImageSelectorProvider } from '../providers/image-selector/image-selector';
import { Facebook } from '@ionic-native/facebook';
import { PostsProvider } from '../providers/posts/posts';
import { SocialSharingProvider } from '../providers/social-sharing/social-sharing';
import { PushNotificationsProvider } from '../providers/push-notifications/push-notifications';
import { GoogleProvider } from '../providers/google/google';
import { JobAdsProvider } from '../providers/job-ads/job-ads';
import { GeolocationProvider } from '../providers/geolocation/geolocation';
import { AdsProvider } from '../providers/ads/ads';
import * as lodash from 'lodash';
import { PlaceServiceProvider } from '../providers/trip-service/place-service';
import { ComponentsModule } from '../components/components.module';
import { AppMinimize } from '@ionic-native/app-minimize';
import { MobileAccessibility } from '@ionic-native/mobile-accessibility';
import { UpdateAppPage } from '../pages/update-app/update-app';
import { FriendshipsProvider } from '../providers/friendships/friendships';
import { MessagesProvider } from '../providers/messages/messages';
import { UiToolsProvider } from '../providers/ui-tools/ui-tools';
import { SettingsProvider } from '../providers/settings/settings';
import { PinnedPostsProvider } from '../providers/posts/pinned-posts';
import { ImageViewerProvider } from '../providers/image-viewer/image-viewer';
import { BadgesProvider } from '../providers/badges/badges';
import { NewsBrowserProvider } from '../providers/news-browser/news-browser';
import { OnboardingProvider } from '../providers/onboarding/onboarding';
import { HintsProvider } from '../providers/hints/hints';
import { AbTestsProvider } from '../providers/ab-tests/ab-tests';
import { ContentToolsProvider } from '../providers/content-tools/content-tools';
import { NativeAccessProvider } from '../providers/native-access/native-access';
import { UserVerificationProvider } from '../providers/user-verification/user-verification';
import { LeaderboardProvider } from '../providers/leaderboard/leaderboard-provider';
import { MediaSelectorProvider } from '../providers/image-selector/media-selector';
import { MediaFileService } from '../providers/image-selector/media-file-service';
import { FilePath } from '@ionic-native/file-path';
import { LiveChatProvider } from '../providers/live-chat/live-chat';
import { TaggingProvider } from '../providers/posts/tagging.provider';
import { NewsPageModule } from '../pages/news/news.module';

Pro.init('6fb48926', {
  appVersion: '0.0.0',
});

@Injectable()
export class MyErrorHandler implements ErrorHandler {
  ionicErrorHandler: IonicErrorHandler;

  constructor(injector: Injector) {
    try {
      this.ionicErrorHandler = injector.get(IonicErrorHandler);
    } catch (e) {
      console.error('could not inject ionicErrorHandler', e);
      // Unable to get the IonicErrorHandler provider, ensure
      // IonicErrorHandler has been added to the providers list below
    }
  }

  handleError(err: any): void {
    if (/PositionError/.test(String(err))) {
      // We are in a testing mode
      console.error('Skipping position error', err);
      return;
    }

    if (/cordova_not_available/.test(String(err)) && !window['cordova']) {
      console.error('Skipping cordova error', err);
      return;
    }

    try {
      // Pro.monitoring.handleNewError(err);
      // Remove this if you want to disable Ionic's auto exception handling
      // in development mode.
      this.ionicErrorHandler && this.ionicErrorHandler.handleError(err);
    } catch (e) {
      console.error('error logging error', e);
    }
  }
}

@NgModule({
  declarations: [MyApp],
  imports: [
    BrowserModule,
    HttpClientModule, 
    HomePageModule,
    NewsPageModule,
    PipesModule,
    ComponentsModule,
    IonicModule.forRoot(MyApp, {
      mode: 'ios',
      navExitApp: false,
      modalEnter: 'modal-slide-in',
      modalLeave: 'modal-slide-out',
      pageTransition: 'ios-transition',
    }),
  ],
  bootstrap: [IonicApp],
  entryComponents: [MyApp],
  providers: [
    Camera,
    DecimalPipe,
    Crop,
    File,
    StatusBar,
    MediaCapture,
    VideoEditor,
    Shake,
    AndroidPermissions,
    ScreenOrientation,
    IonicErrorHandler,
    [{ provide: ErrorHandler, useClass: MyErrorHandler }],
    EnvVariables,
    CrudApiProvider,
    AuthProvider,
    GeolocationProvider,
    LocationAccuracy,
    ToolsProvider,
    UserProvider,
    ConfigProvider,
    TripService,
    ImageSelectorProvider,
    Facebook,
    PostsProvider,
    SocialSharing,
    SocialSharingProvider,
    PushNotificationsProvider,
    GoogleProvider,
    JobAdsProvider,
    AdsProvider,
    PlaceServiceProvider,
    // Contacts,
    AppMinimize,
    AppVersion,
    Deeplinks,
    MobileAccessibility,
    FriendshipsProvider,
    MessagesProvider,
    UiToolsProvider,
    SettingsProvider,
    PinnedPostsProvider,
    ImageViewerProvider,
    BadgesProvider,
    NewsBrowserProvider,
    OnboardingProvider,
    HintsProvider,
    AbTestsProvider,
    ContentToolsProvider,
    NativeAccessProvider,
    UserVerificationProvider,
    LeaderboardProvider,
    MediaSelectorProvider,
    MediaFileService,
    FilePath,
    Chooser,
    LiveChatProvider,
    TaggingProvider,
  ],
})
export class AppModule {}



// WEBPACK FOOTER //
// ./src/app/app.module.ts